import { Box, Stack, SvgIcon, Typography } from '@mui/material'
import PhonelinkLockOutlinedIcon from '@mui/icons-material/PhonelinkLockOutlined'

import Button from '@hapana/shared/web/components/shared/button/button.component'
import { BUTTON_VARIANTS } from '@hapana/shared/web/components/shared/button/button.constants'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'

import { useStringTranslation } from '_components/check-in-widget/policy-gate/use-string-translation.hook'
import { useCancelPendingItemsHandoff } from '_hooks/pending-items-handoff.hooks'
import useAuthStore from '_stores/auth.store'
import { readDeskSessionLockTarget, useDeskSessionLockStore } from '_stores/desk-session-lock.store'

import {
  PENDING_ITEMS_IDS,
  STAFF_LOGIN_PATH,
  buildPendingItemsKey,
} from '../pending-items.constants'
import { styles } from '../pending-items.styles'
import { DeskUnlock } from './desk-lock-screen.component'

const DeskSessionLock = () => {
  const t = useStringTranslation()
  const { openToast } = useToast()
  const unlockDesk = useDeskSessionLockStore((state) => state.unlockDesk)
  const signOut = useAuthStore((state: { signOut: () => Promise<unknown> }) => state.signOut)
  const cancelHandoff = useCancelPendingItemsHandoff()

  // The desk tab keeps its presence binding until the handoff ends, so leaving the lock
  // screen must end the handoff rather than wait out its TTL.
  const releaseDesk = async () => {
    const target = readDeskSessionLockTarget(useDeskSessionLockStore.getState())
    if (target) {
      await cancelHandoff.mutateAsync(target).catch(() =>
        openToast({
          title: t(buildPendingItemsKey('desk.sessionLockCancelFailed')),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
        })
      )
    }
    unlockDesk()
  }

  const handleSignOut = async () => {
    await releaseDesk()
    await signOut()
    window.location.assign(STAFF_LOGIN_PATH)
  }

  return (
    <Box component="main" sx={styles.page}>
      <Box sx={styles.lockedCard} data-testid={PENDING_ITEMS_IDS.deskSessionLock}>
        <Box sx={styles.stateWrapper}>
          <SvgIcon component={PhonelinkLockOutlinedIcon} sx={styles.stateIcon} aria-hidden />
          <Typography variant="header2" component="h1" color="gray.80">
            {t(buildPendingItemsKey('desk.sessionLockTitle'))}
          </Typography>
          <Typography variant="medium" sx={styles.muted}>
            {t(buildPendingItemsKey('desk.sessionLockBody'))}
          </Typography>
        </Box>
        <DeskUnlock onUnlocked={() => void releaseDesk()} />
        <Stack spacing={1}>
          <Typography variant="medium" sx={styles.muted}>
            {t(buildPendingItemsKey('desk.sessionLockSignOutHelp'))}
          </Typography>
          <Box>
            <Button
              variant={BUTTON_VARIANTS.OUTLINED}
              label={t(buildPendingItemsKey('desk.sessionLockSignOut'))}
              onClick={() => void handleSignOut()}
              data-testid={PENDING_ITEMS_IDS.deskSessionSignOut}
            />
          </Box>
        </Stack>
      </Box>
    </Box>
  )
}

export default DeskSessionLock
